import * as React from 'react';
import { GlobalApplicationState } from 'globalApplicationState';
import { ConnectedProps, connect, useSelector } from 'react-redux';
import { TopicDetails } from 'modules/topics';
import { Grid } from '@mui/material';
import { DEFAULT_FIELD_WIDTH, DEFAULT_LABEL_WIDTH, DIALOG_BODY_PADDING } from 'modules/topics/styles/constants';
import Availability from '../../common/availability';
import TopicAudienceChips from '../common/permissions/topicAudienceChips';
import ExclamationHover from 'modules/common/components/hovers/exclamationHover';
import { TagStats } from 'modules/insights';
import { BasicAudience, actions } from 'modules/audiences';
import TopicInsights from './topicInsights';
import EmptyDataMessage from 'modules/insights/components/emptyDataMessage';

const TopicViewDetailsBody: React.FunctionComponent<PropsWithRedux> = (props) => {
    const [audienceDetails, setAudienceDetails] = React.useState<BasicAudience[] | null>(null);

    const { topicGroups } = useSelector((state: GlobalApplicationState) => state.topics);

    const { getAudienceDetails: fetchAudienceDetails } = props;

    React.useEffect(() => {
        const loadAudienceDetails = async (audienceIds: string[]) => {
            try {
                const res = await Promise.all(
                    audienceIds.map(async (a) => {
                        const details = await fetchAudienceDetails(a);
                        return ({ id: a, name: `${details.title} (${details.totalUsers})` });
                    })
                );
                setAudienceDetails(res);
            }
            catch (_) {
                setAudienceDetails([]);
            }
        };
        
        const uniqueAudiences = Array.from(
            new Set([...props.topic.defaultFor.map(a => a.id),
                ...props.topic.mandatoryFor.map(a => a.id),
                ...props.topic.restrictedTo.map(a => a.id)]));
                
        if (audienceDetails == null) loadAudienceDetails(uniqueAudiences);
    }, [audienceDetails, props.topic, fetchAudienceDetails]);

    const getRestricted = (): JSX.Element | null => {
        if (!props.topic.restricted && !props.topic.default && !props.topic.mandatory) {
            return <ExclamationHover> This is a standard topic. No optional settings have been applied.</ExclamationHover>;
        }
        return props.topic.restricted ?
            <span style={{ justifySelf: "start" }} className="material-icons">lock</span>
            : null;
    };

    const getAudienceDetails = (audiences: BasicAudience[]): BasicAudience[] =>
        !audienceDetails ? audiences : audienceDetails.filter(d => audiences.map(a => a.id).includes(d.id));

    const getOverview = (): JSX.Element =>
        <React.Fragment>
            <Grid item xs={DEFAULT_LABEL_WIDTH}>
                Topic name
            </Grid>

            <Grid item xs={DEFAULT_FIELD_WIDTH} sx={{ display: "flex", alignItems: "center" }}>
                <strong>{`${props.topic.name}  `}</strong> {getRestricted()}
            </Grid>

            <Grid item xs={DEFAULT_LABEL_WIDTH}>
                Topic group
            </Grid>

            <Grid item xs={DEFAULT_FIELD_WIDTH}>
                <strong>{
                    topicGroups.find(tg => tg.id === props.topic.groupId)?.name
                }</strong>
            </Grid>

            <Grid item xs={DEFAULT_LABEL_WIDTH}>
                State
            </Grid>

            <Grid item xs={DEFAULT_FIELD_WIDTH} sx={{ paddingLeft: "15px" }}>
                <strong>{
                    <Availability isDisabled={props.topic.disabled || false} />
                }</strong>
            </Grid>

            <Grid item xs={DEFAULT_LABEL_WIDTH}>
                Default for
            </Grid>

            <Grid item xs={DEFAULT_FIELD_WIDTH}>
                <TopicAudienceChips
                    show={props.topic.default}
                    editable={false}
                    selected={getAudienceDetails(props.topic.defaultFor)}
                />
            </Grid>

            <Grid item xs={DEFAULT_LABEL_WIDTH}>
                Mandatory for
            </Grid>

            <Grid item xs={DEFAULT_FIELD_WIDTH}>
                <TopicAudienceChips
                    show={props.topic.mandatory}
                    editable={false}
                    selected={getAudienceDetails(props.topic.mandatoryFor)}
                />
            </Grid>

            <Grid item xs={DEFAULT_LABEL_WIDTH}>
                Restricted to
            </Grid>

            <Grid item xs={DEFAULT_FIELD_WIDTH}>
                <TopicAudienceChips
                    show={props.topic.restricted}
                    editable={false}
                    selected={getAudienceDetails(props.topic.restrictedTo)}
                />
            </Grid>
        </React.Fragment>;

    return (
        <React.Fragment>
            <Grid container
                justifyItems={"start"}
                alignItems={"center"}
                paddingTop={DIALOG_BODY_PADDING}
                paddingBottom={DIALOG_BODY_PADDING}
                rowSpacing={DIALOG_BODY_PADDING}
            >
                <Grid item xs={DEFAULT_LABEL_WIDTH + DEFAULT_FIELD_WIDTH}>
                    <strong>Overview</strong>
                </Grid>
                {getOverview()}

                <Grid item xs={DEFAULT_LABEL_WIDTH + DEFAULT_FIELD_WIDTH}>
                    <strong>Key Insights</strong>
                </Grid>
            </Grid>

            {
                !props.stats
                ? <EmptyDataMessage containerStyle={{padding: "20px"}}/>
                : <TopicInsights
                    topic={props.topic}
                    stats={props.stats}
                    onReport={props.onReport} />
            }
        </React.Fragment>
    );
};

interface ComponentProps {
    topic: TopicDetails;
    stats: TagStats | null;
    onReport: () => void;
}

const connector = connect(
    (_: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps
    }),
    {
        getAudienceDetails: actions.getAudienceDetails
    }
);

type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(TopicViewDetailsBody);